// A project's model files (back/routes/projectModels.js): list, sync from Procore / ACC,
// upload, send, download, zip, export, register.
import { url, tokenRef } from "api";

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

async function json(method, path, body) {
  const r = await fetch(`${url}${path}`, {
    method,
    headers: { ...authHeader(), ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(out.error || out.err || `HTTP ${r.status}`);
  return out;
}

/** Fetch a file and save it under the server's name (or `fallback`). */
async function save(method, path, body, fallback) {
  const r = await fetch(`${url}${path}`, {
    method,
    headers: { ...authHeader(), ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const out = await r.json().catch(() => ({}));
    throw new Error(out.error || `HTTP ${r.status}`);
  }
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = ((r.headers.get("Content-Disposition") || "").match(/filename="?([^";]+)"?/) || [])[1] || fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

const qs = (o) => {
  const p = new URLSearchParams();
  Object.entries(o || {}).forEach(([k, v]) => {
    const s = Array.isArray(v) ? v.join(",") : v;
    if (s) p.set(k, s);
  });
  const t = p.toString();
  return t ? `?${t}` : "";
};

const base = (projectId) => `/project-models/${encodeURIComponent(projectId)}`;

export const listModels = (projectId, filters) => json("GET", `${base(projectId)}${qs(filters)}`);
export const syncModels = (projectId, provider) => json("POST", `${base(projectId)}/sync`, provider ? { provider } : {});
// `to`: a provider ('procore' | 'acc' - its first destination) or a list of destination ids
export const sendModels = (projectId, to, ids) =>
  json("POST", `${base(projectId)}/send`, Array.isArray(to) ? { destinations: to, ids } : { provider: to, ids });
// "Sync with cloud": what each destination has against ours, and doing what was chosen
export const modelSyncPlan = (projectId) => json("GET", `${base(projectId)}/sync-plan`);
export const applyModelSync = (projectId, actions) => json("POST", `${base(projectId)}/sync-apply`, { actions });
// model tunnels: a folder on one Procore / ACC account feeds a folder on another
export const listModelTunnels = (projectId) => json("GET", `${base(projectId)}/tunnels`);
export const addModelTunnel = (projectId, body) => json("POST", `${base(projectId)}/tunnels`, body);
export const saveModelTunnel = (projectId, id, body) => json("PUT", `${base(projectId)}/tunnels/${id}`, body);
export const removeModelTunnel = (projectId, id) => json("DELETE", `${base(projectId)}/tunnels/${id}`);
// Check a tunnel's source now. `includeExisting`: send what is already there as well,
// past the "only new versions" baseline.
export const runModelTunnel = (projectId, id, { includeExisting = false } = {}) =>
  json("POST", `${base(projectId)}/tunnels/${id}/run`, { includeExisting });
// "Export to 3D": a heavy-worker export of each file, each one a new commit of the project
export const publishModels = (projectId, ids) => json("POST", `${base(projectId)}/publish`, { ids });

// ---- WHAT AN EXPORT IS DOING, AND WHAT IT DID --------------------------------------
// Three reads, kept apart on purpose: the first is polled, the second opens a drawer,
// the third only runs when somebody expands the lines. See back/routes/projectModels.js.

/** What is exporting on THIS project right now: { busy, percent, count, files, runs }. */
export const exportActivity = (projectId) => json("GET", `${base(projectId)}/export-activity`);

/** The same, for a page of project cards at once: { [projectId]: { percent, ... } }. */
export const exportActivityFor = (projectIds) =>
  (projectIds && projectIds.length
    ? json("GET", `/project-models/active/summary?projectIds=${projectIds.join(",")}`)
    : Promise.resolve({}));

/** One file's whole export history, newest first. */
export const exportRuns = (projectId, fileId) => json("GET", `${base(projectId)}/${fileId}/export-runs`);

/** One run's log lines; `after` (ISO) returns only what is newer. */
export const exportRunLog = (projectId, runId, after) =>
  json("GET", `${base(projectId)}/export-runs/${runId}/log${after ? `?after=${encodeURIComponent(after)}` : ""}`);
// "Viewpoints to issues": a Navisworks file's saved viewpoints (marked when already issues on
// the target), and a heavy-worker run raising one triage issue per chosen one there
export const listViewpoints = (projectId, id, targetProjectId) =>
  json("GET", `${base(projectId)}/${id}/viewpoints${qs({ targetProjectId })}`);
export const createViewpointIssues = (projectId, id, { targetProjectId, keys, names }) =>
  json("POST", `${base(projectId)}/${id}/viewpoint-issues`, { targetProjectId, keys, names });
export const viewpointIssueRuns = (projectId, id) => json("GET", `${base(projectId)}/${id}/viewpoint-issues/runs`);
export const retryModel = (projectId, id) => json("POST", `${base(projectId)}/${id}/retry`, {});
// APPROVE a version (the current one by default): it goes to every destination with
// "upload approved model versions" on, as the next version of the same file there
export const approveModel = (projectId, id, version) => json("POST", `${base(projectId)}/${id}/approve`, version ? { version } : {});
export const removeModel = (projectId, id) => json("DELETE", `${base(projectId)}/${id}`);
export const downloadModel = (projectId, id, name, version) =>
  save("GET", `${base(projectId)}/${id}/download${version ? `?version=${version}` : ""}`, null, name);
export const downloadModelsZip = (projectId, ids) => save("POST", `${base(projectId)}/bundle`, { ids }, "models.zip");
export const exportModels = (projectId, filters, format) =>
  save("GET", `${base(projectId)}/export${qs({ ...filters, format })}`, null, `models.${format}`);
export const modelsRegister = (projectId, filters) => save("GET", `${base(projectId)}/register${qs(filters)}`, null, "models-register.pdf");

export async function uploadModels(projectId, files) {
  const fd = new FormData();
  [...files].forEach((f) => fd.append("files", f));
  const r = await fetch(`${url}${base(projectId)}/upload`, { method: "POST", headers: authHeader(), body: fd });
  const out = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(out.error || `HTTP ${r.status}`);
  return out;
}
// transfers: what is downloading / uploading / waiting, and pausing or cancelling it
export const listTransfers = (projectId) => json("GET", `${base(projectId)}/transfers`);
export const pauseAllTransfers = (projectId) => json("POST", `${base(projectId)}/transfers/pause-all`);
export const resumeAllTransfers = (projectId) => json("POST", `${base(projectId)}/transfers/resume-all`);
// type 'upload' | 'download', action 'pause' | 'resume' | 'cancel' | 'retry'
export const transferAction = (projectId, type, id, action) =>
  json("POST", `${base(projectId)}/transfers/${encodeURIComponent(type)}/${encodeURIComponent(id)}/${encodeURIComponent(action)}`);
// integrations: every ACC / Procore connection of the project and its switch
export const listIntegrations = (projectId) => json("GET", `${base(projectId)}/integrations`);
export const switchIntegration = (projectId, control, on) => json("POST", `${base(projectId)}/integrations/switch`, { control, on });
